/**
 * TransferPreviewStep - Step 5: Preview da transferência antes da execução
 * 
 * Mostra resumo visual com:
 * - Origem e destino
 * - Quantidade de registros afetados
 * - Avisos sobre transferências pendentes
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { 
  TransferType, 
  TransferFilters, 
  TransferPreviewResult,
  TRANSFER_TYPE_LABELS 
} from '@/hooks/useBulkTransfer';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowRight, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  Loader2,
  Info,
  Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferPreviewStepProps {
  transferType: TransferType;
  fromId: string;
  toId: string;
  filters: TransferFilters;
  previewResult: TransferPreviewResult | null;
  isLoading: boolean;
  error: string | null;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferPreviewStep({
  transferType,
  fromId,
  toId,
  filters,
  previewResult,
  isLoading,
  error,
}: TransferPreviewStepProps) {
  const { currentTenant } = useAuth();

  const isAgentTransfer = transferType === 'conversations' || transferType === 'deals_assigned';
  const isDepartmentTransfer = transferType === 'conversations_department'; // v2.5.0
  
  // Identifica se é transferência de negócios (para mostrar alerta sobre regra de exclusão)
  const isDealTransfer = transferType === 'deals_assigned' || transferType === 'deals_seller';

  // =========================================================================
  // QUERY: DADOS DA ORIGEM
  // =========================================================================

  const { data: fromData } = useQuery({
    queryKey: ['transfer-person', fromId, transferType],
    queryFn: async () => {
      // v2.5.0: Para departamento, buscar da tabela departments
      if (isDepartmentTransfer) {
        const { data } = await supabase
          .from('departments')
          .select('id, name, description')
          .eq('id', fromId)
          .single();
        return data ? { id: data.id, full_name: data.name, email: data.description, avatar_url: null } : null;
      }
      if (isAgentTransfer) {
        const { data } = await supabase
          .from('profiles')
          .select('id, full_name, email, avatar_url')
          .eq('id', fromId)
          .single();
        return data;
      } else {
        const { data } = await supabase
          .from('crm_sellers')
          .select('id, name, email, profiles:user_id(avatar_url)')
          .eq('id', fromId)
          .single();
        return data ? {
          id: data.id,
          full_name: data.name,
          email: data.email,
          avatar_url: (data.profiles as { avatar_url: string | null } | null)?.avatar_url,
        } : null;
      }
    },
    enabled: !!fromId,
  });

  // =========================================================================
  // QUERY: DADOS DO DESTINO
  // =========================================================================

  const { data: toData } = useQuery({
    queryKey: ['transfer-person', toId, transferType],
    queryFn: async () => {
      // v2.5.0: Para departamento, buscar da tabela departments
      if (isDepartmentTransfer) {
        const { data } = await supabase
          .from('departments')
          .select('id, name, description')
          .eq('id', toId)
          .single();
        return data ? { id: data.id, full_name: data.name, email: data.description, avatar_url: null } : null;
      }
      if (isAgentTransfer) {
        const { data } = await supabase
          .from('profiles')
          .select('id, full_name, email, avatar_url')
          .eq('id', toId)
          .single();
        return data;
      } else {
        const { data } = await supabase
          .from('crm_sellers')
          .select('id, name, email, profiles:user_id(avatar_url)')
          .eq('id', toId)
          .single();
        return data ? {
          id: data.id,
          full_name: data.name,
          email: data.email,
          avatar_url: (data.profiles as { avatar_url: string | null } | null)?.avatar_url,
        } : null;
      }
    },
    enabled: !!toId,
  });

  // =========================================================================
  // RENDER
  // =========================================================================

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Calculando registros...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertTitle>Erro ao calcular preview</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const hasRecords = previewResult && previewResult.count > 0;
  const hasPendingTransfers = previewResult && previewResult.pending_transfers > 0;

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Confirme a transferência</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Revise os detalhes antes de executar
        </p>
      </div>

      {/* Card de visualização da transferência */}
      <Card className="border-2">
        <CardContent className="p-6">
          {/* De -> Para */}
          <div className="flex items-center justify-between gap-4">
            {/* Origem */}
            <div className="flex-1 text-center">
              <p className="text-xs text-muted-foreground mb-2">DE</p>
              {isDepartmentTransfer ? (
                <div className="h-16 w-16 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                </div>
              ) : (
                <Avatar className="h-16 w-16 mx-auto mb-2">
                  <AvatarImage src={fromData?.avatar_url || undefined} />
                  <AvatarFallback className="text-lg">
                    {fromData?.full_name?.substring(0, 2).toUpperCase() || '??'}
                  </AvatarFallback>
                </Avatar>
              )}
              <p className="font-medium truncate">{fromData?.full_name || 'Carregando...'}</p>
              <p className="text-xs text-muted-foreground truncate">{fromData?.email}</p>
            </div>

            {/* Seta */}
            <div className="flex-shrink-0">
              <div className="p-3 rounded-full bg-primary/10">
                <ArrowRight className="h-6 w-6 text-primary" />
              </div>
            </div>

            {/* Destino */}
            <div className="flex-1 text-center">
              <p className="text-xs text-muted-foreground mb-2">PARA</p>
              {isDepartmentTransfer ? (
                <div className="h-16 w-16 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
              ) : (
                <Avatar className="h-16 w-16 mx-auto mb-2">
                  <AvatarImage src={toData?.avatar_url || undefined} />
                  <AvatarFallback className="text-lg">
                    {toData?.full_name?.substring(0, 2).toUpperCase() || '??'}
                  </AvatarFallback>
                </Avatar>
              )}
              <p className="font-medium truncate">{toData?.full_name || 'Carregando...'}</p>
              <p className="text-xs text-muted-foreground truncate">{toData?.email}</p>
            </div>
          </div>

          {/* Separador */}
          <div className="my-6 border-t" />

          {/* Resumo de registros */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">
              {TRANSFER_TYPE_LABELS[transferType]}
            </p>
            <p className={cn(
              "text-4xl font-bold",
              hasRecords ? "text-primary" : "text-muted-foreground"
            )}>
              {previewResult?.count || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {previewResult?.will_be_limited 
                ? `registros encontrados (apenas ${previewResult?.transfer_limit} serão transferidos)`
                : 'registros serão transferidos'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* v2.5.0: Alerta quando limite será aplicado */}
      {previewResult?.will_be_limited && (
        <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-600">Limite de transferência</AlertTitle>
          <AlertDescription>
            Existem <strong>{previewResult.count}</strong> registros, mas apenas{' '}
            <strong>{previewResult.transfer_limit}</strong> serão transferidos nesta execução.
            Para transferir os restantes, repita o procedimento.
          </AlertDescription>
        </Alert>
      )}

      {/* Alerta informativo sobre exclusão de negócios ganhos/perdidos */}
      {isDealTransfer && (
        <Alert variant="default" className="border-blue-500/50 bg-blue-500/10">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-600">Regra de transferência</AlertTitle>
          <AlertDescription>
            Negócios com status <strong>Ganho</strong> ou <strong>Perdido</strong> não são 
            incluídos na transferência em massa, pois já foram finalizados.
          </AlertDescription>
        </Alert>
      )}

      {/* Avisos */}
      {!hasRecords && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Nenhum registro encontrado</AlertTitle>
          <AlertDescription>
            Não há registros para transferir com os filtros selecionados.
            Volte e ajuste os filtros ou selecione outra origem.
          </AlertDescription>
        </Alert>
      )}

      {hasPendingTransfers && (
        <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-600">Transferências pendentes</AlertTitle>
          <AlertDescription>
            {previewResult.pending_transfers} conversa(s) têm transferência pendente.
            Elas serão <strong>canceladas</strong> e substituídas por esta nova transferência.
          </AlertDescription>
        </Alert>
      )}

      {hasRecords && !hasPendingTransfers && (
        <Alert variant="default" className="border-green-500/50 bg-green-500/10">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-600">Pronto para transferir</AlertTitle>
          <AlertDescription>
            Clique em "Confirmar Transferência" para executar a operação.
            Esta ação não pode ser desfeita.
          </AlertDescription>
        </Alert>
      )}

      {/* Filtros aplicados (se houver) */}
      {Object.values(filters).some(Boolean) && (
        <div className="text-xs text-muted-foreground text-center">
          Filtros aplicados: {Object.entries(filters)
            .filter(([_, v]) => v)
            .map(([k]) => k.replace('_', ' '))
            .join(', ')}
        </div>
      )}
    </div>
  );
}
